import type { RoleName } from "./groups";

// =========================================================================
// Domain types ported verbatim from the existing nymr-objectives app.
// All consumer pages key off these shapes - the SQL backend's PascalCase
// rows are mapped to camelCase here in src/data/api.ts.
//
// Write payload shapes (PersonFieldCreate, PersonFieldUpdate, etc.) keep
// the PascalCase keys from the original SP-era types so existing page
// code ports without renaming. src/data/api.ts JSON-serialises these
// directly to the new write endpoints, which accept the same shape.
// =========================================================================

export type Team = "Exec" | "CLT" | "User";

/**
 * True when a person should appear in board-facing views
 * (team heatmap, global objectives table, categories, reports).
 *
 * Team = "User" people are self-service only - they can sign in, see and
 * edit their own objectives, but are deliberately excluded from the
 * leadership-focused views above. They still appear on `/cover` for their
 * coverer and on their own `/person/:id` page.
 */
export function isReportablePerson(p: Person): boolean {
  return p.active && (p.team === "Exec" || p.team === "CLT");
}

/**
 * The scope views used by /team and /reports. Both surfaces share the
 * `peopleInScope` helper so they stay in lock-step on what each mode means.
 *
 *   personal      - just me; my own objectives only
 *   myTeam        - self + same-department + people I cover
 *   peopleICover  - self + everyone in me.covers (any team)
 *   execOverview  - active Exec/CLT (+/- Users via includeUsers)
 */
export type ScopeView = "personal" | "myTeam" | "execOverview" | "peopleICover";

/**
 * Normalise a person's FullName for case/whitespace-insensitive matching.
 */
export function normalisePersonName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Compute the set of active person FullName keys (normalised) visible in a
 * given scope view. Shared by /team and /reports so the surfaces stay in
 * lock-step.
 */
export function peopleInScope(
  people: Person[],
  selfPerson: Person | null,
  covers: Person[],
  view: ScopeView,
  includeUsers: boolean,
): Set<string> {
  const set = new Set<string>();
  if (view === "personal") {
    if (selfPerson) set.add(normalisePersonName(selfPerson.fullName));
    return set;
  }
  if (view === "myTeam") {
    if (selfPerson) set.add(normalisePersonName(selfPerson.fullName));
    const myDept = selfPerson?.department ?? null;
    if (myDept) {
      for (const p of people) {
        if (p.active && p.department === myDept) {
          set.add(normalisePersonName(p.fullName));
        }
      }
    }
    for (const p of covers) {
      set.add(normalisePersonName(p.fullName));
    }
    return set;
  }
  if (view === "peopleICover") {
    if (covers.length === 0) return set;
    if (selfPerson) set.add(normalisePersonName(selfPerson.fullName));
    for (const p of covers) {
      set.add(normalisePersonName(p.fullName));
    }
    return set;
  }
  // execOverview
  for (const p of people) {
    if (!p.active) continue;
    if (isReportablePerson(p)) set.add(normalisePersonName(p.fullName));
    else if (includeUsers && p.team === "User") {
      set.add(normalisePersonName(p.fullName));
    }
  }
  return set;
}

export type Department = string;

export interface Person {
  id: number;
  fullName: string;
  team: Team | null;
  department: Department | null;
  jobTitle: string | null;
  active: boolean;
  /** FullName of another Person who maintains this person's objectives on their behalf. */
  coveredBy: string | null;
}

/**
 * Generic taxonomy entry - used for the Categories and Departments lists
 * that back dropdowns across the app.
 */
export interface TaxonomyItem {
  id: number;
  name: string;
}

/**
 * A bi-monthly reporting period tied to the board meeting cycle.
 *
 * All date fields are exposed as `YYYY-MM-DD` strings at the domain level.
 */
export interface ReportingPeriod {
  id: number;
  title: string;
  year: number | null;
  startDate: string | null;
  endDate: string | null;
  boardMeetingDate: string | null;
  boardDeadlineDate: string | null;
  isCurrent: boolean;
}

export type ObjectiveStatus = "Draft" | "Active" | "Deleted";

/**
 * A monthly RAG score against a (PersonName, Category, Year, Month) slot.
 * - `year` is the reporting-year integer (e.g. 2026 means Apr 2026 - Mar 2027).
 * - `month` is the calendar month 1-12.
 * - `ragValue` is encoded as 1=Red, 2=Amber, 3=Green. Null = Not set.
 */
export interface RagScore {
  id: number;
  personName: string;
  category: string;
  year: number;
  month: number;
  ragValue: 1 | 2 | 3 | null;
  narrative: string | null;
  /** Name of the ReportingPeriod this score belongs to, or null if not yet back-filled. */
  reportingPeriodName: string | null;
}

/**
 * A per-person month lock - presence of a row means the person (or someone
 * with edit rights for them) has marked their (year, month) slot as
 * "done editing this month". The lock is a single on/off signal: a row
 * means locked, no row means unlocked. Year is the REPORTING year (matches
 * RagScore.year). The Lock pill on My Objectives toggles the current
 * month; Team Dashboard surfaces it as a small lock icon next to the
 * person's name.
 */
export interface MonthLock {
  id: number;
  personId: number;
  personName: string;
  year: number;
  month: number;
  /** ISO timestamp the lock was created. */
  lockedAt: string;
  /** UPN of the person who created the lock (audit trail). */
  lockedBy: string;
}

export interface Objective {
  id: number;
  personName: string;
  category: string | null;
  year: number | null;
  status: ObjectiveStatus | null;
  objectiveText: string | null;
  measuresText: string | null;
}

export interface CurrentUser {
  displayName: string;
  username: string;
  groupIds: string[];
  role: RoleName;
  matchedPerson: Person | null;
  /** Active people whose CoveredBy matches this user's FullName. */
  covers: Person[];
  canEditFor: (personName: string) => boolean;
  canEdit: (o: Objective) => boolean;
}

// =========================================================================
// Write payload shapes
//
// PascalCase keys preserved from the original SP-era types so the AdminPage
// / CategoriesPage / ObjectiveModal port verbatim without field renames.
// These shapes are what we POST/PATCH to the write endpoints; the API
// translates them to camelCase column updates internally.
// =========================================================================

export interface PersonFieldCreate {
  FullName: string;
  Team?: Team | string | null;
  Department?: string | null;
  JobTitle?: string | null;
  CoveredBy?: string | null;
  Active?: boolean;
}

export interface PersonFieldUpdate {
  FullName?: string;
  Team?: Team | string | null;
  Department?: string | null;
  JobTitle?: string | null;
  CoveredBy?: string | null;
  Active?: boolean;
}

export interface ObjectiveFieldCreate {
  PersonName: string;
  Category?: string | null;
  Year?: number | null;
  Status?: ObjectiveStatus | null;
  ObjectiveText?: string | null;
  MeasuresText?: string | null;
}

export interface ObjectiveFieldUpdate {
  PersonName?: string;
  Category?: string | null;
  Year?: number | null;
  Status?: ObjectiveStatus | null;
  ObjectiveText?: string | null;
  MeasuresText?: string | null;
}

export interface RagScoreFieldCreate {
  PersonName: string;
  Category: string;
  Year: number;
  Month: number;
  RagValue?: 1 | 2 | 3 | null;
  Narrative?: string | null;
  ReportingPeriodName?: string | null;
}

export interface RagScoreFieldUpdate {
  RagValue?: 1 | 2 | 3 | null;
  Narrative?: string | null;
  Category?: string;
  ReportingPeriodName?: string | null;
}

export interface ReportingPeriodFieldCreate {
  Title: string;
  Year?: number | null;
  StartDate?: string | null;
  EndDate?: string | null;
  BoardMeetingDate?: string | null;
  BoardDeadlineDate?: string | null;
  IsCurrent?: boolean;
}

export interface ReportingPeriodFieldUpdate {
  Title?: string;
  Year?: number | null;
  StartDate?: string | null;
  EndDate?: string | null;
  BoardMeetingDate?: string | null;
  BoardDeadlineDate?: string | null;
  IsCurrent?: boolean;
}
